import React, { useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
} from "recharts";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload?.length) {
    const d = payload?.[0]?.payload;
    return (
      <div
        className="rounded-lg border p-3 text-xs"
        style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-lg)" }}
      >
        <p className="font-heading font-semibold mb-1" style={{ color: d?.color }}>{d?.name}</p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          Engagement: <span className="font-data font-medium" style={{ color: "var(--color-foreground)" }}>{d?.engagement}/100</span>
        </p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          Value Score: <span className="font-data font-medium" style={{ color: "var(--color-foreground)" }}>{d?.value}/100</span>
        </p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          Customers: <span className="font-data font-medium" style={{ color: "var(--color-foreground)" }}>{d?.customers?.toLocaleString()}</span>
        </p>
      </div>
    );
  }
  return null;
};

const quadrantLabels = [
  { x: 75, y: 75, label: "High Value\nHigh Engage", color: "#059669" },
  { x: 25, y: 75, label: "High Value\nLow Engage", color: "#DC2626" },
  { x: 75, y: 25, label: "Low Value\nHigh Engage", color: "#D97706" },
  { x: 25, y: 25, label: "Low Value\nLow Engage", color: "#6B7280" },
];

export default function SegmentationMatrix({ segments: segmentsProp }) {
  const [activeSegment, setActiveSegment] = useState(null);
  const segmentData = (segmentsProp || []).map((s) => ({
    ...s,
    size: s.customers,
  }));

  return (
    <div
      className="rounded-xl border"
      style={{
        background: "var(--color-card)",
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-md)",
        padding: "20px",
      }}
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base lg:text-lg" style={{ color: "var(--color-foreground)" }}>
            Customer Segmentation Matrix
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            Engagement vs. Value quadrant analysis — bubble size = customer count
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {segmentData?.slice(0, 5)?.map((s) => (
            <button
              key={s?.name}
              onClick={() => setActiveSegment(activeSegment === s?.name ? null : s?.name)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full font-caption text-xs transition-all duration-150"
              style={{
                background: activeSegment === s?.name ? `${s?.color}22` : "var(--color-muted)",
                color: activeSegment === s?.name ? s?.color : "var(--color-text-secondary)",
                border: `1px solid ${activeSegment === s?.name ? s?.color : "transparent"}`,
                minHeight: 28,
              }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: s?.color }} />
              {s?.name}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0 h-72 lg:h-80" aria-label="Customer segmentation bubble chart">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                type="number"
                dataKey="engagement"
                name="Engagement"
                domain={[0, 100]}
                label={{ value: "Engagement Score", position: "insideBottom", offset: -10, fontSize: 11, fill: "var(--color-text-secondary)" }}
                tick={{ fontSize: 10, fill: "var(--color-text-secondary)" }}
              />
              <YAxis
                type="number"
                dataKey="value"
                name="Value"
                domain={[0, 100]}
                label={{ value: "Value Score", angle: -90, position: "insideLeft", offset: 15, fontSize: 11, fill: "var(--color-text-secondary)" }}
                tick={{ fontSize: 10, fill: "var(--color-text-secondary)" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine x={50} stroke="var(--color-border)" strokeDasharray="4 4" strokeWidth={1.5} />
              <ReferenceLine y={50} stroke="var(--color-border)" strokeDasharray="4 4" strokeWidth={1.5} />
              <Scatter data={segmentData} onClick={(d) => setActiveSegment(activeSegment === d?.name ? null : d?.name)}>
                {segmentData?.map((s, i) => (
                  <Cell
                    key={i}
                    fill={`${s?.color}55`}
                    stroke={s?.color}
                    strokeWidth={activeSegment === s?.name ? 2.5 : 1.5}
                    r={Math.sqrt(s?.size / Math.PI) * 1.8}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Segment legend */}
        <div className="lg:w-52 xl:w-60 flex-shrink-0">
          <p className="font-caption text-xs font-semibold mb-2" style={{ color: "var(--color-text-secondary)" }}>
            All Segments
          </p>
          <div className="flex flex-col gap-1.5 overflow-y-auto" style={{ maxHeight: 300 }}>
            {segmentData?.map((s) => (
              <button
                key={s?.name}
                onClick={() => setActiveSegment(activeSegment === s?.name ? null : s?.name)}
                className="flex items-center justify-between px-2.5 py-2 rounded-lg transition-all duration-150 text-left"
                style={{
                  background: activeSegment === s?.name ? `${s?.color}18` : "var(--color-surface-2)",
                  border: `1px solid ${activeSegment === s?.name ? s?.color : "var(--color-border)"}`,
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s?.color }} />
                  <span className="font-caption text-xs truncate" style={{ color: "var(--color-foreground)" }}>
                    {s?.name}
                  </span>
                </div>
                <span className="font-data text-xs font-medium flex-shrink-0 ml-2" style={{ color: "var(--color-text-secondary)" }}>
                  {s?.customers}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}